import * as toolbox from 'components/common/toolbox';
import firebase from 'firebase/compat/app';
import { APPS } from 'components/constants';
import { NOTIFICATION_MESSAGES, NOTIFICATIONS } from './action-types';
import { navMultipleUrls } from 'components/common/toolbox';

const firestore = firebase.firestore();

//-------------------------------------------------------------------------------------------
// NOTIFICATION MESSAGES
//-------------------------------------------------------------------------------------------

/**
 * Subscribes to live updates of the notifications collection.
 * 
 * @param { string } handle
 * @returns { Function }
 */
export function subNotificationMessages(handle) {

	return dispatch => {

		dispatch({ type: NOTIFICATION_MESSAGES + '_PENDING' });

		const unsubscribe = firestore.collection(handle + '/notifications/messages').orderBy('lastSent', 'desc').limit(100).onSnapshot((snapshot) => {
			
            var messages = [];
            for (const doc of snapshot.docs) {
                messages.push({ ...doc.data(), id: doc.id });
            }

			dispatch({ type: NOTIFICATION_MESSAGES + '_FULFILLED', data: messages, unsubscribe });

		}, (error) => {
            console.error(`There was an error subscribing to ${handle}/notifications/messages: `, error);
        });
	};
}

/**
 * Saves notification messages to the Firestore database.
 *
 * @param {string} handle - The Firestore collection handle (base path).
 * @param {string} id - The ID of the message. If '0', a new message is created.
 * @param {Object} message - The message object to save or update.
 * @param {Function} [callback] - Optional callback function to execute after saving.
 *                                Receives the message ID as an argument.
 * @returns {Function} A thunk function to be executed by Redux Thunk middleware.
 */
export function saveNotificationMessages(handle, id, message, callback) {

	return async dispatch => {

        try {
		    dispatch({ type: NOTIFICATION_MESSAGES + '_SAVE_PENDING' });

            if (id == '0') {
                message.created = new Date();
                message.modified = new Date();
                message.lastSent = null;

                id = await firestore.collection(handle + '/notifications/messages').add(message);
            } else {
                await firestore.collection(handle + '/notifications/messages').doc(id).update(message);
            }

            dispatch({ type: NOTIFICATION_MESSAGES + '_SAVE_FULFILLED' });
            window.toastr.success(`The Message was successfully saved`, `Message saved!`);
			if (typeof callback === 'function') callback(id);

        } catch(error) {
            window.toastr.error(`The Message was unsuccessful`, `Message NOT saved`);
            console.error('Error update save message: ', error)
        }
	};
}

/**
 * Deletes a notification message from the Firestore database
 *
 * @param {string} handle - The handle or path prefix for the Firestore collection.
 * @param {string} id - The ID of the notification message to delete.
 * @param {Function} [callback] - An optional callback function to execute after the message is deleted.
 * @returns {Function} A thunk function that performs the asynchronous deletion and dispatches actions.
 */
export function deleteNotificationMessages(handle, id, callback) {

	return async dispatch => {

        try {
		    dispatch({ type: NOTIFICATION_MESSAGES + '_SAVE_PENDING' });

            await firestore.collection(handle + '/notifications/messages').doc(id).delete();

            dispatch({ type: NOTIFICATION_MESSAGES + '_SAVE_FULFILLED' });
            window.toastr.success(`The Message was successfully deleted`, `Message Deleted!`);
			if (typeof callback === 'function') callback(id);

        } catch(error) {
            window.toastr.error(`The Message deletion was unsuccessful`, `Message NOT Deleted`);
            console.error('Error update save message: ', error)
        }
	};
}

export function sendNotificationMessage(handle, company, message) {

	return async dispatch => {

        try {
		    dispatch({ type: NOTIFICATION_MESSAGES + '_SAVE_PENDING' });

            var queryRef = firestore.collection(`${handle}/profiles/profiles`).where('type', '==', 'employee').where('deleted', '==', false);
            if (message.roleIds?.length) queryRef = queryRef.where('roles', 'array-contains-any', message.roleIds);
            if (message.departmentIds?.length) queryRef = queryRef.where('departmentId', 'in', message.departmentIds);
            const querySnapshot = await queryRef.get();

            var profiles = [];
            querySnapshot.docs.forEach(doc => {
                profiles.push({ ...doc.data(), id: doc.id });
            });

            
            for (let profile of profiles) {
                
                const contactDocs = await firestore.collection(`${handle}/contacts/contacts`).where('profileIds', 'array-contains', profile.id).limit(1).get();
                const contacts = contactDocs.docs.map(doc => ({ ...doc.data(), id: doc.id }));
                const contact = contacts[0];
                
                await firestore.collection(`${handle}/notifications/notifications`).add({
                    userId: contact.email,
                    name: `${contact.firstName} ${contact.lastName}`,
                    email: contact.email,
                    mobile: (message.urgent) ? contact?.mobile : null,
                    subject: message.name,
                    message: message.message,
                    fromSms: (company.smsPhoneNumber) ? company.smsPhoneNumber : '',
                    fromEmail: 'no-reply@enspirex.com',
                    mediaUrl: (company.smsLogo) ? [ company.smsLogo ] : [],
                    date: new Date(),
                    read: false,
                });
            }

            await firestore.collection(`${handle}/notifications/messages`).doc(message.id).update({ lastSent: new Date() });

            dispatch({ type: NOTIFICATION_MESSAGES + '_SAVE_FULFILLED' });
            window.toastr.success(`The Messages were successfully queued for delivery`, `Messages Sent!`);

        } catch(error) {
            window.toastr.error(`Sending the Message was unsuccessful`, `Message NOT Deleted`);
            console.error('Error update save message: ', error)
        }
	};
}

//-------------------------------------------------------------------------------------------
// NOTIFICATIONS
//-------------------------------------------------------------------------------------------

/**
 * Subscribes to live updates of the notifications collection.
 * 
 * @param { string } handle
 * @param { string } emmail
 * @returns { Function }
 */
export function subNotifications(handle, email) {

	return dispatch => {

		dispatch({ type: NOTIFICATIONS + '_PENDING' });

		const unsubscribe = firestore.collection(handle + '/notifications/notifications').where('userId', '==', email).orderBy('date', 'desc').limit(100).onSnapshot((snapshot) => {
			
            var notifications = []
            for (const doc of snapshot.docs) {
                notifications.push({ ...doc.data(), id: doc.id });
            }

			dispatch({ type: NOTIFICATIONS + '_FULFILLED', data: notifications, unsubscribe });

		}, (error) => {
            console.error(`There was an error subscribing to ${handle}/notifications/notifications: `, error);
        });
	};
}

/**
 * Marks a notification as read. 
 * 
 * This means the user has clicked on the notification and redirected to the entity id specified in notification.
 * 
 * @async
 * @param { string } handle 
 * @param { string } notification_id
 */
export function markNotificationAsRead(handle, notification_id) {
    return async dispatch => {

        dispatch({ type: NOTIFICATIONS + '_SAVE_PENDING' });
        
        await firestore.collection(`${handle}/notifications/notifications`).doc(notification_id).update({ read: true });
        
        dispatch({ type: NOTIFICATIONS + '_SAVE_FULFILLED' });
    }
}

/**
 * Clears all unread notifications by marking them read individually via a batch request.
 * 
 * @async
 * @param { string } handle 
 * @param { array } unread_notifications
 */
export function clearAllUnreadNotifications(handle, unread_notifications) {
    return () => {
        
        const batch = firestore.batch();
        const collectionRef = firestore.collection(`${handle}/notifications/notifications`);
        for (const notification of unread_notifications) {
            const doc = collectionRef.doc(notification.id)
            batch.update(doc, { read: true })
        }

        batch.commit();
    }
} 

/**
 * Generates a customer notification and stores it in the Firestore database.
 *
 * @param {Object} params - The parameters for the notification.
 * @param {string} params.handle - The handle or identifier for the Firestore collection path.
 * @param {string} params.id - The unique identifier for the notification.
 * @param {string} params.message - The message content of the notification.
 * @param {string[]} params.mobileArray - An array of mobile numbers to send the notification to.
 * @param {string} [params.photoUrl] - The URL of the photo to include in the notification (e.g., technician's photo).
 * @param {string} [params.smsLogo] - The URL of the logo to include in the notification.
 * @param {string} params.smsPhoneNumber - The phone number to display as the sender of the SMS.
 * @param {string} params.type - The type entiry for the notification (e.g., "work-order").
 * @returns {Function} A function that, when executed, sends the notification to the specified mobile numbers.
 */
export function generateCustomerNotification({ 
    customerId,
    handle, 
    id, 
    message, 
    mobileArray, 
    photoUrl,
    smsLogo, 
    smsPhoneNumber,
    technicianId, 
    type 
}) {
    return async () => {

        try {

            for (let mobile of mobileArray) {
        
                await firestore.collection(`${handle}/notifications/notifications`).add({
                    customerId: customerId,
                    date: new Date(),
                    fromEmail: 'no-reply@enspirex.com',
                    fromSms: smsPhoneNumber, 
                    id: id,
                    mediaUrl: [
                        ...(photoUrl) ? [photoUrl] : [], // Photo of the technician
                        ...(smsLogo) ? [smsLogo] : [], // Logo of the company
                    ],
                    message: message,
                    mobile: '+1' + mobile.replace(/\D/g, ''), // Remove non-digit characters,
                    read: false,
                    source: 'technician',
                    subject: 'Technician on the way',
                    technicianId: technicianId,
                    type: type,
                });
                window.toastr.success(`A notification has been generated and sent to ${mobile}`, `SMS Notification Sent`);
            }
        } catch(error) {
            window.toastr.error(`The notification was unsuccessful`, `SMS Notification NOT Sent`);
            console.error('Error update read property on notification: ', error)
        }
    }
}

export function viewDetails({
    entity, 
    entityId, 
    actions_service_requests,
    // Standard params
    actions_users, 
    dispatch, 
    history, 
    numPanels, 
    params, 
    user,
}) {
    return async() => {
        
        const serviceRequest = await actions_service_requests.getServiceRequestById(params.handle, entityId);
        const appId = (serviceRequest.type == 'asset_request') ? APPS.ASSETS.id : APPS.DISPATCH.id;

        if (params.appId != appId) {
            if (!user.dismissed?.appNotice) {
                toolbox.switchAppsNotice((isDismissed) => {
                    if (isDismissed) dispatch(actions_users.dismissNotice(params.handle, user.email, 'appNotice'));
                }, () => proceed());
            } else proceed();
        } else proceed();

        function proceed() {
            toolbox.serviceRequestURL(params.handle, appId, serviceRequest, (locations) => {
                navMultipleUrls({ actions_users, dispatch, history, numPanels, params, user, urlArray: locations});
            });
        }
    }
} 
